import './File.scss'

import ByteConverter from 'byte-converter-react'
import React, {useCallback} from 'react'
import {FileError, FileRejection, useDropzone} from 'react-dropzone'
import {useTranslation} from '../../../util/i18n'
import {style} from '../../../util/style'
import {FileRejections} from '../../blocks/form/FileRejections'
import {Icon} from '../../ui/Icon'

const styles = style('file', ['item', 'errors'])

type File = {
	filesize: number // Filesize in Mb
	filetypes: Array<string> // list of accepted filetypes [.pdf,.png]
	multiple: boolean
	name: string
	onChange: (val: any) => void
	placeholder: string
	required: boolean
	serverErrors: Array<string>
	value: Array<any>
}

export const File: React.FC<File> = ({
	filesize,
	filetypes,
	multiple,
	name,
	onChange,
	placeholder,
	required,
	serverErrors,
	value = []
}) => {
	const onDrop = useCallback(
		(acceptedFiles) => {
			if (multiple) {
				const currentFiles = new Set(
					value.map((v) => `${v.path}${v.name}${v.size}`)
				)

				const newFiles = []
				acceptedFiles.forEach((file) => {
					if (!currentFiles.has(`${file.path}${file.name}${file.size}`)) {
						newFiles.push(file)
					}
				})

				onChange([...value, ...newFiles])
			} else {
				onChange(acceptedFiles)
			}
		},
		[value, multiple]
	)

	const {fileRejections, getRootProps, getInputProps} = useDropzone({
		onDrop,
		multiple,
		accept: filetypes,
		maxSize: (filesize || 2) * 1000000 // 1 megabyte (MB) = 1 000 000 bytes (b)
	})

	return (
		<div className={styles()}>
			<input required={required} name={name} {...getInputProps()} />
			<div {...getRootProps()} className={styles('input')} id={name}>
				<span className={styles('input-icon')}>
					<Icon icon="upload" />
				</span>
				{placeholder || 'Select a file'}
			</div>
			{value && value.length > 0 && (
				<ul className={styles('files')}>
					{value.map((file) => (
						<FormBlockFileItem
							key={`${file.name}-${file.size}`}
							name={file.name}
							size={file.size}
							onDelete={() => {
								onChange([...value].filter((f) => f !== file))
							}}
						/>
					))}
				</ul>
			)}
			<FormBlockFilesErrors
				fileRejections={fileRejections}
				serverErrors={serverErrors}
				maxSizeMb={filesize || 2}
			/>
		</div>
	)
}

const FormBlockFileItem: React.FC<{
	name: string
	size: number
	onDelete: () => void
}> = ({name, size, onDelete}) => {
	const t = useTranslation('form').file

	return (
		<li className={styles.item()}>
			<div className={styles.item('left')}>
				<p className={styles.item('left-name')}>{name}</p>
			</div>
			<div className={styles.item('right')}>
				<div className={styles.item('right-size')}>
					<ByteConverter
						suffix
						inUnit="B"
						outUnit={size > 1000 * 1000 ? 'MB' : 'KB'}
						useSI
					>
						{size}
					</ByteConverter>
				</div>
				<a onClick={onDelete} className={styles.item('right-link')}>
					{t.delete}
				</a>
			</div>
		</li>
	)
}

const FormBlockFilesErrors: React.FC<{
	fileRejections: Array<FileRejection>
	maxSizeMb: number
	serverErrors: Array<string>
}> = ({fileRejections, serverErrors, maxSizeMb}) => {
	const hasRejections: boolean = fileRejections.length > 0
	const hasServerErrors: boolean = serverErrors.length > 0

	if (!hasRejections && !hasServerErrors) return null

	const rejections = new Set<
		| FileRejection
		| {file: {name: string; size: number}; errors: Array<FileError>}
	>(fileRejections)

	if (hasServerErrors) {
		serverErrors.forEach((error) => {
			const parsedError: Array<{
				file: {name: string; size: number}
				errors: Array<FileError>
			}> = JSON.parse(error)
			parsedError.forEach(
				(e: {file: {name: string; size: number}; errors: Array<FileError>}) => {
					rejections.add(e)
				}
			)
		})
	}

	return (
		<div className={styles.errors()}>
			<FileRejections fileRejections={[...rejections]} maxSizeMb={maxSizeMb} />
		</div>
	)
}
